<script setup lang="ts">
import { computed, h, ref, watch, type VNodeChild } from 'vue';
import {
  NButton,
  NCollapse,
  NCollapseItem,
  NFlex,
  NIcon,
  NTooltip,
  type SelectOption,
} from 'naive-ui';
import { IconAdd, IconCheck, IconCopy, IconInfo } from '@/components/design/icons';
import {
  TemplateFieldType,
  type AnnotationFilter,
  type AnnotationInstance,
  type FieldCatalogEntity,
  type FieldCatalogFilter,
  type FieldCatalogFilterArg,
  type RenderTagRequest,
} from '@/api';
import {
  type AnnotationDraft,
  type BlockKeyword,
  buildBlockFieldName,
  filtersForType,
  isFormatFilterDef,
  joinForExpression,
  parseBlockTag,
  renderJinjaPreview,
  splitForExpression,
} from '@/composables/contractGeneration/useTemplateAnnotations';
import { TemplateAuthoringService } from '@/services/contractGeneration/templateAuthoringService';
import {
  gaiaFontSize,
  gaiaFontWeight,
  gaiaFonts,
  gaiaNeutral,
  gaiaStatus,
} from '@/components/design/themes/tokens';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import GaiaSwitch from '@/components/design/base/GaiaSwitch.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaDateField from '@/components/design/base/inputs/GaiaDateField.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaInputNumber from '@/components/design/base/inputs/GaiaInputNumber.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { annotationConfigStyleVars, annotationConfigTitle } from './annotationConfig';

/**
 * A tag's settings: group or role, sub-field or field name, default value,
 * selection options, output filters, and a live preview of the Jinja it
 * renders. The form is the same wherever it sits — inside the configure
 * drawer (Word add-in, snippet library) or embedded in the Fields page under
 * "Settings". Apply hands the caller a patch; nothing is written here.
 */
interface Props {
  annotation: AnnotationDraft | null;
  filterCatalog: FieldCatalogFilter[];
  /** Type of the field being configured — drives filter eligibility. */
  fieldType?: TemplateFieldType | null;
  /** Existing entity/object aliases — feed into the alias dropdown. */
  instances?: AnnotationInstance[];
  /** Catalog of entities — populates the sub-field picker for entity bindings. */
  entityCatalog?: FieldCatalogEntity[];
  /**
   * When true, the form shows a synthetic draft not backed by a chip — the
   * "preview & copy a snippet" flow. Hides Remove/Apply, keeps the preview
   * and the Copy button.
   */
  previewOnly?: boolean;
  /**
   * Show the "Don't show in document" switch (Word add-in). When on, the
   * caller inserts the field as a non-printing `{% set %}` statement.
   */
  showHidden?: boolean;
  /** Initial value of that switch, for callers that persist the hidden flag. */
  initialHidden?: boolean;
  /**
   * Whether the form is on screen. The drawer passes its `show` so the fields
   * re-read the annotation each time it opens; an embedded form is always
   * active and re-reads when the annotation changes.
   */
  active?: boolean;
  /** Embedded in a page rather than a drawer: own header, static preview, Reset instead of Cancel. */
  embedded?: boolean;
  /**
   * Which of a tag's settings this form edits.
   * - 'all': everything — the drawer (Word add-in, snippet library).
   * - 'field': the Fields view — default value and options, the field's,
   *   written to every tag. No filters, no identity.
   * - 'occurrence': the Document view — this tag's output filters, or a
   *   section tag's expression. Default and options are the field's and live
   *   under Fields; name, group and sub-field come from the Word document.
   */
  scope?: 'all' | 'field' | 'occurrence';
  /**
   * How many tags of the same field the document has, this one included.
   * Above 1, each filter card offers to put that filter on all of them.
   */
  siblingCount?: number;
  /**
   * Filters every one of those tags already carries.
   *
   * Broadcasting is a one-shot merge that leaves no flag behind, so "all
   * places" can only be answered by looking: a filter is on all places when it
   * is on all places. Read once per open to seed the cards, then owned by the
   * author until they apply again.
   */
  sharedFilterNames?: string[];
}

interface Emits {
  /**
   * `options.hidden` is set when the "Don't show in document" switch is on;
   * `options.broadcastFilters` lists the filters the author asked to put on
   * every tag of the field (the patch itself is still this tag's).
   */
  (
    e: 'apply',
    patch: Partial<AnnotationDraft>,
    options?: { hidden?: boolean; broadcastFilters?: AnnotationFilter[] }
  ): void;
  (e: 'remove'): void;
  /** The host should close or dismiss; embedded forms reset instead. */
  (e: 'cancel'): void;
}

const props = withDefaults(defineProps<Props>(), {
  fieldType: null,
  instances: () => [],
  entityCatalog: () => [],
  previewOnly: false,
  showHidden: false,
  initialHidden: false,
  active: true,
  embedded: false,
  scope: 'all',
  siblingCount: 1,
  sharedFilterNames: () => [],
});
const emit = defineEmits<Emits>();

const fieldName = ref<string>('');
const filters = ref<AnnotationFilter[]>([]);
const instanceAlias = ref<string>('');
const newAlias = ref<string>('');
const useNewAlias = ref<boolean>(false);
/** Editable sub-field name — only relevant when binding.kind === 'object'. */
const objectSubField = ref<string>('');
/** Selected sub-field name when binding.kind === 'entity' (must come from catalog). */
const entitySubField = ref<string>('');
/**
 * Fallback value used by the render pipeline when the field has no value at
 * generation time. Stored on the annotation as `default_value`. Doesn't
 * apply to block tags — `{% if %}` / `{% for %}` aren't substitution sites.
 */
const defaultValue = ref<string>('');
/**
 * Allowed values when the field is of type 'selection'. Rendered as the
 * `choices(...)` filter at materialize time. Empty for any other type.
 */
const selectionOptions = ref<string[]>([]);
/**
 * "Don't show in document" — when on, the caller inserts a non-printing
 * `{% set _ = … %}` statement (the field is still collected as input, just
 * not rendered). Only surfaced when `showHidden` is set.
 */
const hidden = ref<boolean>(false);

const binding = computed(() => props.annotation?.binding ?? null);
const isEntityBinding = computed(() => binding.value?.kind === 'entity');
const isObjectBinding = computed(() => binding.value?.kind === 'object');
const isInstanceBinding = computed(() => isEntityBinding.value || isObjectBinding.value);

const isBlockStart = computed(() => props.annotation?.kind === 'block_start');
const isBlockMiddle = computed(() => props.annotation?.kind === 'block_middle');
const isBlockEnd = computed(() => props.annotation?.kind === 'block_end');
const isBlockTag = computed(() => isBlockStart.value || isBlockMiddle.value || isBlockEnd.value);

/**
 * A block tag discovered in the source `.docx` (vs. authored in the app).
 * Its inner expression IS editable — saving rewrites the tag text in the
 * source — but it can't be deleted here: the backend's materializer rejects
 * removing block tags (an opener can't be dropped without its closer). So we
 * only use this flag to hide the Remove button for these tags.
 */
const isExtractedBlock = computed<boolean>(
  () => isBlockTag.value && props.annotation?.origin === 'extracted'
);

/** Editable inner expression for non-`for` block tags — e.g. `is_vip_client`. */
const blockExpression = ref<string>('');
const blockKeyword = ref<BlockKeyword>('if');
/** For `for` loops only — left half of the `<var> in <iterable>` expression. */
const forLoopVar = ref<string>('');
/** For `for` loops only — right half of the `<var> in <iterable>` expression. */
const forLoopIterable = ref<string>('');

const isForLoop = computed(() => blockKeyword.value === 'for');

const isFieldScope = computed(() => props.scope === 'field');
const isOccurrenceScope = computed(() => props.scope === 'occurrence');
/**
 * Name, group/role and sub-field come from the Word document itself. On the
 * Fields page they are read-only; only the standalone drawer still edits them.
 */
const showIdentityEditor = computed(() => props.scope === 'all');
/** Default value and options are the field's, edited from the field list. */
const showFieldValues = computed(() => !isOccurrenceScope.value);
/** Output filters are one tag's own, edited from the document. */
const showFilters = computed(() => !isFieldScope.value);
/** A field used in several places: each filter can be put on every tag of it. */
const canApplyToAll = computed(() => isOccurrenceScope.value && props.siblingCount > 1);

/**
 * What the preview and the payload carry as filters: the field's shared
 * settings carry none (each tag adds its own), and a selection never carries
 * any — it renders through the `choices(...)` the backend appends.
 */
const previewFilters = computed<AnnotationFilter[]>(() =>
  isFieldScope.value || isSelection.value ? [] : filters.value
);

const title = computed<string>(() => {
  if (isBlockTag.value || props.scope === 'all') return annotationConfigTitle(props.annotation);
  return isFieldScope.value ? 'Field value' : 'Display at this place';
});

/** One line under the embedded title saying how far a change reaches. */
const subtitle = computed<string>(() => {
  if (isBlockTag.value || props.scope === 'all') return '';
  return isFieldScope.value
    ? 'Applies at every place this field appears in the document.'
    : 'Formatting for this tag. Other places keep theirs unless you apply to all.';
});

/** The per-card "Only here | All N places" choice. */
const scopeOptions = computed<GaiaSegmentedOption[]>(() => [
  { label: 'Only here', value: 'one' },
  { label: `All ${props.siblingCount} places`, value: 'all' },
]);

/** A filter card leads with what the filter does, in words; the name comes second. */
function filterCardTitle(name: string): string {
  const description = filterDef(name)?.description.trim() ?? '';
  return description ? description.replace(/\.$/, '') : name;
}

/** Type-aware placeholder for the default-value control. */
const defaultValuePlaceholder = computed<string>(() => {
  const type = effectiveFieldType.value;
  switch (type) {
    case TemplateFieldType.NUMBER:
      return 'e.g. 0';
    case TemplateFieldType.DATE:
      return 'e.g. 3 Feb 2026';
    case TemplateFieldType.BOOLEAN:
      return 'Yes or no';
    case TemplateFieldType.STRING:
      return 'e.g. N/A';
    default:
      return 'e.g. placeholder text';
  }
});

// The default is stored as text — that is what the tag carries — but the
// control that edits it follows the field's type, as in the wizard: a number
// box, a date field, a yes/no choice, the options of a selection. The text
// input stays for plain text and for types with no better control.
const isNumberField = computed<boolean>(
  () => effectiveFieldType.value === TemplateFieldType.NUMBER
);
const isDateField = computed<boolean>(() => effectiveFieldType.value === TemplateFieldType.DATE);
const isBooleanField = computed<boolean>(
  () => effectiveFieldType.value === TemplateFieldType.BOOLEAN
);

const defaultNumberValue = computed<number | null>({
  get: () => {
    const raw = defaultValue.value.trim();
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  },
  set: value => {
    defaultValue.value = value === null ? '' : String(value);
  },
});

/**
 * The tag stores an ISO "YYYY-MM-DD" — the shape GaiaDateField reads and
 * writes, so the field's typed input and its calendar both land here already
 * normalised.
 */
const defaultDateValue = computed<string | null>({
  get: () => {
    const raw = defaultValue.value.trim();
    return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
  },
  set: iso => {
    defaultValue.value = iso ?? '';
  },
});

const BOOLEAN_DEFAULT_OPTIONS: SelectOption[] = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
];

/** Every accepted spelling maps onto its option; anything else shows as unset. */
const defaultBooleanValue = computed<string | null>({
  get: () => {
    const raw = defaultValue.value.trim().toLowerCase();
    if (raw === 'true' || raw === 'yes' || raw === '1') return 'true';
    if (raw === 'false' || raw === 'no' || raw === '0') return 'false';
    return null;
  },
  set: value => {
    defaultValue.value = value ?? '';
  },
});

const bindingPath = computed<string>(() => {
  const b = binding.value;
  if (!b) return '';
  if (b.kind === 'entity') {
    const sub = entitySubField.value || b.sub_field;
    return `${b.entity_type}.${sub}`;
  }
  if (b.kind === 'object') {
    const sub = objectSubField.value.trim() || b.sub_field;
    return `object.${sub}`;
  }
  return '';
});

/**
 * Live preview state. Declared before the re-init watcher below: that watcher
 * runs immediately, and an embedded form is active from its first render, so
 * `refreshPreview()` touches these during setup — a `let` declared further
 * down would still be in its temporal dead zone and throw.
 */
const livePreview = ref<string>('');
const previewLoading = ref<boolean>(false);
/** Backend render error (e.g. a filter rejecting the sample value). */
const previewError = ref<string | null>(null);
let previewSeq = 0;
let previewTimer: number | null = null;
/**
 * Filters (by name) showing "all N tags" on their card.
 *
 * Seeded from the ones that already sit on every tag, so reopening reports
 * where a filter actually is rather than forgetting what was done last time.
 */
const broadcastFilterNames = ref<Set<string>>(new Set());

/** Load the fields from the annotation as it currently stands. */
function resetFromAnnotation(): void {
  if (!props.annotation) return;
  fieldName.value = props.annotation.field_name ?? '';
  defaultValue.value = props.annotation.default_value ?? '';
  hidden.value = props.initialHidden;
  // Only the ones this tag carries: a filter on every *other* tag but not this
  // one is not "applied to all" from here, it is simply absent.
  const onThisTag = new Set((props.annotation.filters ?? []).map(filter => filter.name));
  broadcastFilterNames.value = new Set(props.sharedFilterNames.filter(name => onThisTag.has(name)));
  selectionOptions.value = [...(props.annotation.selection_options ?? [])];
  filters.value = (props.annotation.filters ?? []).map(item => ({
    ...item,
    args: [...(item.args ?? [])],
  }));
  if (binding.value?.kind === 'entity') {
    instanceAlias.value = binding.value.instance_alias;
    entitySubField.value = binding.value.sub_field;
    objectSubField.value = '';
  } else if (binding.value?.kind === 'object') {
    instanceAlias.value = binding.value.instance_alias;
    objectSubField.value = binding.value.sub_field;
    entitySubField.value = '';
  } else {
    instanceAlias.value = '';
    objectSubField.value = '';
    entitySubField.value = '';
  }
  newAlias.value = '';
  useNewAlias.value = false;
  if (isBlockTag.value) {
    const parsed = parseBlockTag(props.annotation);
    blockKeyword.value = parsed.keyword;
    if (parsed.keyword === 'for') {
      const split = splitForExpression(parsed.expression);
      forLoopVar.value = split.loopVar;
      forLoopIterable.value = split.iterable;
      blockExpression.value = '';
    } else {
      blockExpression.value = parsed.expression;
      forLoopVar.value = '';
      forLoopIterable.value = '';
    }
  } else {
    blockExpression.value = '';
    blockKeyword.value = 'if';
    forLoopVar.value = '';
    forLoopIterable.value = '';
  }
  refreshPreview();
}

// Re-read on scope changes too: the same tag can be shown for its field and
// then on its own, and edits not applied in one must not linger in the other.
watch(
  () => [props.active, props.annotation?.local_id, props.scope],
  () => {
    if (!props.active || !props.annotation) return;
    resetFromAnnotation();
  },
  { immediate: true }
);

/** Filters relevant to the current field's type — anything else is hidden. */
const activeEntity = computed<FieldCatalogEntity | null>(() => {
  const b = binding.value;
  if (b?.kind !== 'entity') return null;
  return props.entityCatalog.find(e => e.name === b.entity_type) ?? null;
});

const entitySubFieldOptions = computed<SelectOption[]>(() =>
  (activeEntity.value?.fields ?? []).map(field => ({
    label: field.name,
    value: field.name,
  }))
);

const effectiveFieldType = computed<TemplateFieldType | null>(() => {
  if (activeEntity.value && entitySubField.value) {
    const match = activeEntity.value.fields.find(f => f.name === entitySubField.value);
    if (match) return match.type;
  }
  return props.fieldType ?? null;
});

const isSelection = computed<boolean>(
  () => effectiveFieldType.value === TemplateFieldType.SELECTION
);

/**
 * Non-empty, trimmed options — what we actually send to the backend and use
 * for the default-value dropdown. The on-screen list can hold blank rows
 * (the user is still typing); we just don't propagate them.
 */
const cleanSelectionOptions = computed<string[]>(() =>
  selectionOptions.value.map(option => option.trim()).filter(option => option.length > 0)
);

/** Default-value dropdown options when authoring a selection field. */
const selectionDefaultOptions = computed<SelectOption[]>(() =>
  cleanSelectionOptions.value.map(option => ({ label: option, value: option }))
);

function addSelectionOption(): void {
  selectionOptions.value = [...selectionOptions.value, ''];
}

function updateSelectionOption(index: number, value: string): void {
  selectionOptions.value = selectionOptions.value.map((item, i) => (i === index ? value : item));
}

function removeSelectionOption(index: number): void {
  selectionOptions.value = selectionOptions.value.filter((_, i) => i !== index);
}

/**
 * Backend rejects a default that isn't one of the options. Surface that as a
 * client-side warning so the user fixes it before hitting Apply.
 */
const selectionDefaultInvalid = computed<boolean>(() => {
  if (!isSelection.value) return false;
  const value = defaultValue.value.trim();
  if (!value) return false;
  return !cleanSelectionOptions.value.includes(value);
});

/** Accepted spellings for a boolean default. */
const BOOLEAN_DEFAULT_LITERALS = new Set(['true', 'false', 'yes', 'no', '1', '0']);

/** Strict YYYY-MM-DD calendar-date check (rejects impossible dates like 2026-02-31). */
function isValidIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
  );
}

/**
 * The optional default value must be compatible with the field's type — a
 * default of "abc" for a number field (or a non-option for a selection) is
 * caught here so it can't be saved, rather than failing later at render time.
 * Returns a user-facing reason, or null when the default is empty (it's
 * optional) or valid.
 */
const defaultValueError = computed<string | null>(() => {
  const value = defaultValue.value.trim();
  if (!value) return null;
  if (isSelection.value) {
    return selectionDefaultInvalid.value
      ? 'Default must be one of the options listed above.'
      : null;
  }
  switch (effectiveFieldType.value) {
    case TemplateFieldType.NUMBER:
      return Number.isFinite(Number(value))
        ? null
        : 'Default must be a number (e.g. 1500 or 12.5).';
    case TemplateFieldType.DATE:
      return isValidIsoDate(value)
        ? null
        : 'Default must be a date in YYYY-MM-DD format (e.g. 2026-01-01).';
    case TemplateFieldType.BOOLEAN:
      return BOOLEAN_DEFAULT_LITERALS.has(value.toLowerCase())
        ? null
        : 'Default must be true or false.';
    default:
      return null; // string / container types accept any text
  }
});

const eligibleFilters = computed<FieldCatalogFilter[]>(() =>
  filtersForType(props.filterCatalog, effectiveFieldType.value)
);

/**
 * "Format" filters (`format_currency`, `format_date`, `format_number`,
 * `format_ordinal`, `format_cardinal`) are the locale/formatting family — the
 * catalog groups them under category `custom`. They each produce a final
 * presentation string and don't compose, so a field may carry **at most one**:
 * the picker disables the rest once one is applied, and `addFilter` refuses a
 * second as a safety net.
 */
function isFormatFilterName(name: string): boolean {
  return isFormatFilterDef(props.filterCatalog.find(item => item.name === name));
}
const hasFormatFilter = computed<boolean>(() =>
  filters.value.some(filter => isFormatFilterName(filter.name))
);

// Label only carries the name; rich rendering (description, example) is
// produced by `renderFilterOption` below. Keeping the label short keeps the
// `filterable` search responsive and the selected display tidy.
const filterOptions = computed<SelectOption[]>(() =>
  eligibleFilters.value.map(item => ({
    label: item.name,
    value: item.name,
    // Only one format filter per field — disable the others once one is applied.
    disabled: isFormatFilterDef(item) && hasFormatFilter.value,
  }))
);

/**
 * Custom dropdown row for each filter — two short lines (name + description)
 * plus a tiny example pill so users can pick the right filter at a glance.
 * Scoped styles don't reach the select's portal, so the visuals are inline.
 */
function renderFilterOption(option: SelectOption): VNodeChild {
  const filter = filterDef(option.value as string);
  if (!filter) return String(option.label ?? '');
  const example = filter.examples.at(0);
  return h(
    'div',
    {
      style: 'display: flex; flex-direction: column; gap: 2px; padding: 4px 0; min-width: 0;',
    },
    [
      h(
        'span',
        {
          style: `font-family: ${gaiaFonts.mono}; font-weight: ${gaiaFontWeight.semibold}; color: ${gaiaNeutral.ink}; font-size: ${gaiaFontSize.row};`,
        },
        filter.name
      ),
      h(
        'span',
        {
          style: `color: ${gaiaNeutral.textSubtle}; font-size: ${gaiaFontSize.meta}; line-height: 1.4; white-space: normal;`,
        },
        filter.description || ''
      ),
      example
        ? h(
            'span',
            {
              style: `font-size: ${gaiaFontSize.meta}; color: ${gaiaNeutral.textSubtle}; font-family: ${gaiaFonts.mono}; white-space: normal;`,
            },
            `${example.input} → ${example.output}`
          )
        : null,
      option.disabled
        ? h(
            'span',
            {
              style: `font-size: ${gaiaFontSize.meta}; color: ${gaiaStatus.warning}; white-space: normal;`,
            },
            'A format filter is already applied — only one is allowed.'
          )
        : null,
    ]
  );
}

const eligibleFilterByName = computed(
  () => new Map(eligibleFilters.value.map(item => [item.name, item]))
);

function filterDef(name: string): FieldCatalogFilter | undefined {
  return (
    eligibleFilterByName.value.get(name) ?? props.filterCatalog.find(item => item.name === name)
  );
}

function addFilter(name: string): void {
  const def = filterDef(name);
  // At most one format filter per field — they don't chain.
  if (isFormatFilterDef(def) && hasFormatFilter.value) {
    message.warning(
      'Only one format filter can be applied to a field. Remove the current one first to switch.'
    );
    return;
  }
  const argDefs = def?.args ?? [];
  // Variadic filters (e.g. `choices`) take an arbitrary number of values
  // through a single declared arg. Seed one empty row so the user has a
  // visible slot to start typing into.
  const initialArgs = isVariadicArgList(argDefs) ? [''] : argDefs.map(arg => arg.default ?? '');
  filters.value = [...filters.value, { name, args: initialArgs }];
  refreshPreview();
}

function updateFilterArg(filterIndex: number, argIndex: number, value: string): void {
  filters.value = filters.value.map((filter, i) => {
    if (i !== filterIndex) return filter;
    const args = [...(filter.args ?? [])];
    args[argIndex] = value;
    return { ...filter, args };
  });
  refreshPreview();
}

function removeFilter(filterIndex: number): void {
  const removed = filters.value[filterIndex]?.name;
  filters.value = filters.value.filter((_, i) => i !== filterIndex);
  if (removed && !filters.value.some(filter => filter.name === removed)) {
    setBroadcast(removed, false);
  }
  refreshPreview();
}

/** Tick or untick "all N tags" on a filter card. */
function setBroadcast(name: string, on: boolean): void {
  const next = new Set(broadcastFilterNames.value);
  if (on) next.add(name);
  else next.delete(name);
  broadcastFilterNames.value = next;
}

/**
 * Variadic filters declare a single arg with `variadic: true` (e.g.
 * `choices("a", "b", "c")`). The runtime args array can have any length,
 * each entry being one option. We render those as a row-based options
 * editor instead of the usual fixed-shape args UI.
 */
function isVariadicArgList(argDefs: FieldCatalogFilter['args']): boolean {
  return (argDefs ?? []).some(arg => arg.variadic === true);
}

function isVariadicFilter(name: string): boolean {
  return isVariadicArgList(filterDef(name)?.args);
}

function variadicArgDef(name: string): FieldCatalogFilterArg | null {
  return filterDef(name)?.args?.find(arg => arg.variadic === true) ?? null;
}

function addFilterOption(filterIndex: number): void {
  filters.value = filters.value.map((filter, i) => {
    if (i !== filterIndex) return filter;
    return { ...filter, args: [...(filter.args ?? []), ''] };
  });
  refreshPreview();
}

function removeFilterOption(filterIndex: number, optionIndex: number): void {
  filters.value = filters.value.map((filter, i) => {
    if (i !== filterIndex) return filter;
    return { ...filter, args: (filter.args ?? []).filter((_, j) => j !== optionIndex) };
  });
  refreshPreview();
}

const aliasOptions = computed<SelectOption[]>(() => {
  const kind: 'entity' | 'object' = isEntityBinding.value ? 'entity' : 'object';
  const entityType = binding.value?.kind === 'entity' ? binding.value.entity_type : null;
  return props.instances
    .filter(inst => {
      if (inst.kind !== kind) return false;
      if (entityType && inst.entity_type !== entityType) return false;
      return true;
    })
    .map(inst => ({ label: inst.alias, value: inst.alias }));
});

const effectiveAlias = computed(() =>
  useNewAlias.value ? newAlias.value.trim() : instanceAlias.value.trim()
);

/**
 * Preview as it will actually be inserted: the rendered `{{ … }}` tag, or a
 * non-printing `{% set _ = … %}` statement when "Don't show in document" is
 * on. Reactive to the toggle — no backend refetch needed.
 */
const displayedPreview = computed<string>(() => {
  const base = livePreview.value || offlineFallbackPreview();
  if (!hidden.value) return base;
  const match = base.match(/^\{\{\s*([\s\S]*?)\s*\}\}$/);
  const expr = match ? match[1] : base;
  return expr ? `{% set _ = ${expr} %}` : base;
});

const message = useGaiaToast();
/** Briefly flips to true after a successful copy so the button shows ✓. */
const copied = ref<boolean>(false);
let copiedTimer: number | null = null;

async function copyPreview(): Promise<void> {
  const text = displayedPreview.value;
  if (!text) return;
  try {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older Safari / sandboxed contexts. `execCommand` is
      // deprecated but it's still the only sync clipboard primitive in
      // browsers that don't expose `navigator.clipboard`.
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      document.execCommand('copy');
      ta.remove();
    }
    copied.value = true;
    if (copiedTimer !== null) window.clearTimeout(copiedTimer);
    copiedTimer = window.setTimeout(() => {
      copied.value = false;
    }, 1500);
  } catch {
    message.error('Could not copy to clipboard.');
  }
}

function buildBindingForPayload(): RenderTagRequest['binding'] {
  const b = binding.value;
  if (!b) return null;
  if (b.kind === 'entity') {
    return {
      kind: 'entity',
      entity_type: b.entity_type,
      instance_alias: effectiveAlias.value || b.instance_alias,
      sub_field: entitySubField.value || b.sub_field,
    };
  }
  if (b.kind === 'object') {
    return {
      kind: 'object',
      instance_alias: effectiveAlias.value || b.instance_alias,
      sub_field: objectSubField.value.trim() || b.sub_field,
    };
  }
  return { kind: 'primitive' };
}

function currentBlockExpression(): string {
  if (isForLoop.value) {
    return joinForExpression(forLoopVar.value, forLoopIterable.value);
  }
  return blockExpression.value.trim();
}

function buildPreviewPayload(): RenderTagRequest {
  if (isBlockTag.value) {
    const expr = currentBlockExpression();
    return {
      kind: props.annotation?.kind ?? 'block_start',
      field_name: buildBlockFieldName(blockKeyword.value, expr),
      binding: null,
      filters: [],
      condition: expr || null,
    };
  }
  return {
    kind: props.annotation?.kind ?? 'inline',
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
    field_name: fieldName.value || props.annotation?.field_name || null,
    binding: buildBindingForPayload(),
    filters: previewFilters.value,
    // `condition` and `required` aren't user-editable on inline tags — the
    // backend infers required from enclosing if-blocks and condition will
    // get its own UI later. Carry through whatever was on the annotation.
    condition: props.annotation?.condition ?? null,
    default_value: defaultValue.value.trim() || null,
    selection_options:
      isSelection.value && cleanSelectionOptions.value.length > 0
        ? cleanSelectionOptions.value
        : null,
  };
}

function offlineFallbackPreview(): string {
  if (!props.annotation) return '';
  return renderJinjaPreview({
    ...props.annotation,
    field_name: fieldName.value || props.annotation.field_name,
    binding: buildBindingForPayload(),
    filters: previewFilters.value,
  });
}

function refreshPreview(): void {
  if (!props.annotation) return;
  if (previewTimer !== null) window.clearTimeout(previewTimer);
  previewTimer = window.setTimeout(() => {
    void runPreviewFetch();
  }, 200);
}

async function runPreviewFetch(): Promise<void> {
  if (!props.annotation) return;
  const seq = ++previewSeq;
  previewLoading.value = true;
  livePreview.value = offlineFallbackPreview();
  try {
    const result = await TemplateAuthoringService.renderTag(buildPreviewPayload());
    if (seq !== previewSeq) return;
    livePreview.value = result;
    previewError.value = null;
  } catch (error) {
    if (seq !== previewSeq) return;
    // Surface the backend's reason (e.g. a filter rejecting the sample value)
    // while keeping the offline fallback visible so the snippet is still shown.
    previewError.value = error instanceof Error ? error.message : 'Failed to render preview.';
  } finally {
    if (seq === previewSeq) previewLoading.value = false;
  }
}

watch(
  [
    fieldName,
    instanceAlias,
    newAlias,
    useNewAlias,
    objectSubField,
    entitySubField,
    blockExpression,
    blockKeyword,
    forLoopVar,
    forLoopIterable,
    defaultValue,
    selectionOptions,
  ],
  () => {
    refreshPreview();
  },
  { deep: true }
);

function apply(): void {
  if (!props.annotation) return;

  if (isBlockTag.value) {
    const expr = currentBlockExpression();
    const patch: Partial<AnnotationDraft> = {
      field_name: buildBlockFieldName(blockKeyword.value, expr),
      condition: expr || null,
    };
    emit('apply', patch);
    return;
  }

  // The patch carries only what this scope edits: a field's shared settings
  // never include a tag's filters, and a tag's settings never include the
  // field's identity, default or options.
  const patch: Partial<AnnotationDraft> = {};

  if (showFieldValues.value) {
    // Don't let an incompatible default value through — it would fail at render.
    if (defaultValueError.value) {
      message.warning(defaultValueError.value);
      return;
    }
    patch.default_value = defaultValue.value.trim() || null;
    patch.selection_options =
      isSelection.value && cleanSelectionOptions.value.length > 0
        ? cleanSelectionOptions.value
        : null;
  }

  // Selection fields render through a strict `choices(...)` filter the
  // backend appends itself — any other filters on top are unsupported, so
  // we drop them on apply.
  if (showFilters.value) {
    patch.filters = isSelection.value ? [] : filters.value;
  }

  if (showIdentityEditor.value) {
    // `required` and `condition` aren't user-editable here — backend infers
    // required from enclosing if-blocks, and condition gets its own UI later.
    // Omitting them from the patch preserves whatever was on the annotation.
    const b = binding.value;
    if (isInstanceBinding.value && b) {
      const alias = effectiveAlias.value;
      if (b.kind === 'entity') {
        patch.binding = {
          kind: 'entity',
          entity_type: b.entity_type,
          instance_alias: alias || b.instance_alias,
          sub_field: entitySubField.value || b.sub_field,
        };
      } else if (b.kind === 'object') {
        patch.binding = {
          kind: 'object',
          instance_alias: alias || b.instance_alias,
          sub_field: objectSubField.value.trim() || b.sub_field,
        };
      }
      patch.field_name = null;
    } else {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
      patch.field_name = fieldName.value.trim() || props.annotation.field_name || 'field';
    }
  }
  const options: { hidden?: boolean; broadcastFilters?: AnnotationFilter[] } = {
    hidden: hidden.value,
  };
  if (canApplyToAll.value && !isSelection.value) {
    const broadcast = filters.value
      .filter(filter => broadcastFilterNames.value.has(filter.name))
      .map(filter => ({ name: filter.name, args: [...(filter.args ?? [])] }));
    if (broadcast.length > 0) options.broadcastFilters = broadcast;
  }
  emit('apply', patch, options);
}

/** Cancel dismisses the drawer; an embedded form has nothing to dismiss, so it resets. */
function handleCancel(): void {
  if (props.embedded) {
    resetFromAnnotation();
    return;
  }
  emit('cancel');
}

function getFilterArgs(filterName: string): FieldCatalogFilter['args'] {
  return filterDef(filterName)?.args ?? [];
}

function getFilterExamples(filterName: string): NonNullable<FieldCatalogFilter['examples']> {
  return filterDef(filterName)?.examples ?? [];
}

function humanizeArgName(name: string): string {
  const KNOWN: Record<string, string> = {
    locale: 'Locale',
    length: 'Length',
    currency: 'Currency',
    iso_3166_alpha_2: 'Country code',
    decimals: 'Decimals',
    precision: 'Precision',
    format: 'Format',
  };
  if (KNOWN[name]) return KNOWN[name];
  return name.replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Normalise a user-typed identifier so it's a valid Jinja variable segment:
 * collapses any run of whitespace to a single `_`. Used on field-name, role,
 * group, and manual subfield inputs — all of which become parts of a Jinja
 * variable name where spaces would break rendering.
 */
function sanitizeIdentifier(value: string): string {
  return value.replace(/\s+/g, '_');
}

/** SelectOption carrying the catalog choice's optional help text. */
type ArgSelectOption = SelectOption & { description?: string | null };

/**
 * Dropdown options for a constrained filter arg, sourced straight from the
 * backend field catalog (`arg.choices`). Args without choices render as a
 * free-text input instead.
 */
function optionsForArg(argDef: FieldCatalogFilterArg): ArgSelectOption[] {
  return (argDef.choices ?? []).map(choice => ({
    label: choice.label,
    value: choice.value,
    description: choice.description,
  }));
}

/**
 * Render a choice as label + muted description. Applied to every menu row
 * (including the selected one) so the list stays consistent.
 */
function renderArgOptionLabel(option: SelectOption): VNodeChild {
  const description = (option as ArgSelectOption).description;
  if (!description) return option.label as string;
  return h('div', { class: 'arg-option' }, [
    h('span', { class: 'arg-option__label' }, `${option.label as string}: `),
    h('span', { class: 'arg-option__desc' }, description),
  ]);
}

const styleVars = annotationConfigStyleVars;
</script>

<template>
  <div class="config-form" :class="{ 'config-form--embedded': embedded }" :style="styleVars">
    <!-- The drawer draws its own header; an embedded form names what it edits
         and says how far a change reaches. -->
    <n-flex v-if="embedded" vertical :size="2" class="config-form__title">
      <gaia-text size="body" weight="semibold" tone="ink">{{ title }}</gaia-text>
      <gaia-text v-if="subtitle" size="meta" tone="subtle">{{ subtitle }}</gaia-text>
    </n-flex>

    <n-flex v-if="annotation" vertical class="config-drawer__body" :size="16">
      <!-- Block-control tags: edit only the inner expression. -->
      <template v-if="isBlockTag">
        <section class="config-drawer__binding">
          <div class="config-drawer__binding-summary">
            <span class="config-drawer__label">Section</span>
            <code class="config-drawer__binding-path">
              {{ isForLoop ? 'Repeat for each item' : 'Show only if condition is met' }}
              {{ isBlockEnd ? '(end)' : '' }}
            </code>
          </div>
          <template v-if="isForLoop && !isBlockEnd">
            <label class="config-drawer__field">
              <span class="config-drawer__label">Name each item as</span>
              <gaia-input v-model:value="forLoopVar" placeholder="e.g. service" size="medium" />
              <span class="config-drawer__field-hint">
                How you'll refer to a single item inside the section.
              </span>
            </label>
            <div class="config-drawer__inline-keyword">
              <span class="config-drawer__keyword-pill">from</span>
            </div>
            <label class="config-drawer__field">
              <span class="config-drawer__label">List to repeat</span>
              <gaia-input
                v-model:value="forLoopIterable"
                placeholder="e.g. services"
                size="medium"
              />
              <span class="config-drawer__field-hint">
                The list of items the section should loop over.
              </span>
            </label>
          </template>
          <label v-else-if="!isBlockEnd && blockKeyword !== 'else'" class="config-drawer__field">
            <span class="config-drawer__label">Show only when</span>
            <gaia-input
              v-model:value="blockExpression"
              placeholder="e.g. is_vip_client"
              size="medium"
            />
            <span class="config-drawer__field-hint">
              The section is included only when this is true at render time.
            </span>
          </label>
          <p v-else-if="!isBlockEnd" class="config-drawer__readonly-note">
            This is the "otherwise" branch — its content is shown when none of the conditions above
            are met. There's nothing to configure here.
          </p>
          <p v-else class="config-drawer__readonly-note">
            This marks where the
            {{ blockKeyword === 'endfor' ? 'repeating' : 'conditional' }} section ends. To change
            the section, edit or remove the opening tag.
          </p>
        </section>
      </template>

      <!-- Entity / object: alias picker + sub-field. The drawer also shows the
           bound path; the Fields page has the field's path in view already. -->
      <section
        v-else-if="showIdentityEditor && isInstanceBinding && binding"
        class="config-drawer__binding"
      >
        <div v-if="scope === 'all'" class="config-drawer__binding-summary">
          <span class="config-drawer__label">Inserts</span>
          <code class="config-drawer__binding-path" :title="bindingPath">
            {{ bindingPath }}
          </code>
        </div>
        <label class="config-drawer__field">
          <span class="config-drawer__label">
            {{ isObjectBinding ? 'Group' : 'Role' }}
          </span>
          <div class="config-drawer__alias-row">
            <gaia-select
              v-if="!useNewAlias"
              :options="aliasOptions"
              :value="instanceAlias || null"
              :placeholder="isObjectBinding ? 'Pick an existing group' : 'Pick an existing role'"
              size="medium"
              filterable
              clearable
              style="flex: 1 1 auto"
              @update:value="value => (instanceAlias = (value as string) ?? '')"
            />
            <gaia-input
              v-else
              :value="newAlias"
              :placeholder="
                isObjectBinding ? 'e.g. contract, invoice' : 'e.g. seller, buyer, witness'
              "
              size="medium"
              @update:value="value => (newAlias = sanitizeIdentifier(value))"
            />
            <n-button size="medium" quaternary @click="useNewAlias = !useNewAlias">
              {{ useNewAlias ? 'Pick existing' : isObjectBinding ? '+ New group' : '+ New role' }}
            </n-button>
          </div>
          <span class="config-drawer__field-hint">
            {{
              isObjectBinding
                ? 'A label that groups related details together (e.g. "contract", "invoice").'
                : 'Which party this refers to in the contract (e.g. "seller", "buyer").'
            }}
          </span>
        </label>
        <label v-if="isObjectBinding" class="config-drawer__field">
          <span class="config-drawer__label">Subfield</span>
          <gaia-input
            :value="objectSubField"
            placeholder="e.g. reference_number"
            size="medium"
            @update:value="value => (objectSubField = sanitizeIdentifier(value))"
          />
          <span class="config-drawer__field-hint">
            The specific piece of information from this group (e.g. "reference_number",
            "signed_date").
          </span>
        </label>
        <label v-else-if="isEntityBinding" class="config-drawer__field">
          <span class="config-drawer__label">Subfield</span>
          <gaia-select
            :options="entitySubFieldOptions"
            :value="entitySubField || null"
            placeholder="Pick a subfield (e.g. name, address)"
            size="medium"
            filterable
            @update:value="value => (entitySubField = (value as string) ?? '')"
          />
          <span class="config-drawer__field-hint">
            Which piece of information about this party should appear here.
          </span>
        </label>
      </section>

      <!-- Primitive: variable name input -->
      <section v-else-if="showIdentityEditor" class="config-drawer__section">
        <label class="config-drawer__field">
          <span class="config-drawer__label">Field name</span>
          <gaia-input
            :value="fieldName"
            placeholder="e.g. seller_name"
            size="medium"
            @update:value="value => (fieldName = sanitizeIdentifier(value))"
          />
          <span class="config-drawer__field-hint">
            A short name for this placeholder. Used when filling in the form later.
          </span>
        </label>
      </section>

      <!-- Inline-only fields below — hidden for block tags. -->
      <template v-if="!isBlockTag">
        <section v-if="showFieldValues && isSelection" class="config-drawer__section">
          <header class="config-drawer__section-header">
            <span class="config-drawer__section-title">
              Options
              <span v-if="cleanSelectionOptions.length" class="config-drawer__count-pill">
                {{ cleanSelectionOptions.length }}
              </span>
            </span>
          </header>
          <div class="selection-options">
            <div
              v-for="(option, index) in selectionOptions"
              :key="index"
              class="selection-options__row"
            >
              <span class="selection-options__handle">{{ index + 1 }}.</span>
              <gaia-input
                :value="option"
                :placeholder="`Option ${index + 1}`"
                size="medium"
                style="flex: 1 1 auto"
                @update:value="value => updateSelectionOption(index, value)"
              />
              <gaia-delete-button
                intent="remove"
                :subject="`option ${index + 1}`"
                @click="removeSelectionOption(index)"
              />
            </div>
            <n-button
              size="small"
              dashed
              block
              class="selection-options__add"
              @click="addSelectionOption"
            >
              <template #icon>
                <n-icon><IconAdd /></n-icon>
              </template>
              Add option
            </n-button>
            <p v-if="selectionOptions.length === 0" class="selection-options__empty">
              No options yet. Add the choices users will pick from.
            </p>
          </div>
        </section>

        <section v-if="showFieldValues" class="config-drawer__section">
          <label class="config-drawer__field">
            <span class="config-drawer__label">Default value</span>
            <gaia-select
              v-if="isSelection"
              :options="selectionDefaultOptions"
              :value="defaultValue || null"
              :placeholder="
                selectionOptions.length === 0
                  ? 'Add options above first'
                  : 'Pick one of the options'
              "
              :disabled="selectionOptions.length === 0"
              :status="defaultValueError ? 'error' : undefined"
              size="medium"
              clearable
              @update:value="value => (defaultValue = (value as string) ?? '')"
            />
            <gaia-input-number
              v-else-if="isNumberField"
              v-model:value="defaultNumberValue"
              :show-button="false"
              :placeholder="defaultValuePlaceholder"
              :status="defaultValueError ? 'error' : undefined"
              size="medium"
              clearable
              style="width: 100%"
            />
            <gaia-date-field
              v-else-if="isDateField"
              v-model:value="defaultDateValue"
              :placeholder="defaultValuePlaceholder"
              :status="defaultValueError ? 'error' : undefined"
              size="medium"
            />
            <gaia-select
              v-else-if="isBooleanField"
              v-model:value="defaultBooleanValue"
              :options="BOOLEAN_DEFAULT_OPTIONS"
              :placeholder="defaultValuePlaceholder"
              size="medium"
              clearable
            />
            <gaia-input
              v-else
              v-model:value="defaultValue"
              :placeholder="defaultValuePlaceholder"
              :status="defaultValueError ? 'error' : undefined"
              size="medium"
            />
            <span
              v-if="defaultValueError"
              class="config-drawer__field-hint config-drawer__field-hint--error"
            >
              {{ defaultValueError }}
            </span>
            <span v-else class="config-drawer__field-hint">
              Optional. Used when this field is left blank.
            </span>
          </label>
        </section>

        <p v-if="isOccurrenceScope && isSelection" class="config-drawer__readonly-note">
          A selection shows its options as its only formatting — there is nothing to add for this
          tag.
        </p>

        <section v-if="showFilters && !isSelection" class="config-drawer__section">
          <header class="config-drawer__section-header">
            <span class="config-drawer__section-title">
              Formatting
              <span v-if="filters.length" class="config-drawer__count-pill">
                {{ filters.length }}
              </span>
            </span>
          </header>

          <gaia-select
            :options="filterOptions"
            :placeholder="
              eligibleFilters.length
                ? 'Add formatting…'
                : 'No formatting available for this kind of value'
            "
            :disabled="eligibleFilters.length === 0"
            size="medium"
            filterable
            clearable
            :value="null"
            :render-label="renderFilterOption"
            @update:value="value => value && addFilter(value as string)"
          />

          <div v-if="filters.length === 0" class="config-drawer__filters-empty">
            <n-icon :size="14" class="config-drawer__filters-empty-icon">
              <IconInfo />
            </n-icon>
            <span>
              Formatting changes how the value is shown — currency formatting turns
              <code>1500</code> into <code>€1,500.00</code>, uppercase turns
              <code>contract</code> into <code>CONTRACT</code>. Pick one above.
            </span>
          </div>

          <div v-else class="config-drawer__filter-cards">
            <div
              v-for="(filter, filterIndex) in filters"
              :key="`${filter.name}-${filterIndex}`"
              class="filter-card"
            >
              <div class="filter-card__head">
                <div class="filter-card__head-text">
                  <span class="filter-card__title">{{ filterCardTitle(filter.name) }}</span>
                  <code class="filter-card__name">{{ filter.name }}</code>
                </div>
                <gaia-delete-button
                  intent="remove"
                  size="xs"
                  subject="formatting"
                  @click="removeFilter(filterIndex)"
                />
              </div>

              <!-- Variadic filters (e.g. `choices`) take an open-ended
                   list of values, rendered as a row-based options editor
                   that edits `filter.args` directly as a string[]. -->
              <div
                v-if="isVariadicFilter(filter.name)"
                class="filter-card__args filter-card__args--variadic"
              >
                <div class="filter-card__arg-header">
                  <span class="filter-card__arg-label">
                    {{ humanizeArgName(variadicArgDef(filter.name)?.name ?? 'Options') }}
                  </span>
                </div>
                <div class="filter-card__variadic">
                  <div
                    v-for="(_, optionIndex) in filter.args ?? []"
                    :key="optionIndex"
                    class="filter-card__variadic-row"
                  >
                    <span class="filter-card__variadic-handle"> {{ optionIndex + 1 }}. </span>
                    <gaia-input
                      :value="filter.args?.[optionIndex] ?? ''"
                      :placeholder="`Option ${optionIndex + 1}`"
                      size="small"
                      style="flex: 1 1 auto"
                      @update:value="value => updateFilterArg(filterIndex, optionIndex, value)"
                    />
                    <gaia-delete-button
                      intent="remove"
                      size="xs"
                      :subject="`option ${optionIndex + 1}`"
                      @click="removeFilterOption(filterIndex, optionIndex)"
                    />
                  </div>
                  <n-button
                    size="small"
                    dashed
                    block
                    class="filter-card__variadic-add"
                    @click="addFilterOption(filterIndex)"
                  >
                    <template #icon>
                      <n-icon><IconAdd /></n-icon>
                    </template>
                    Add option
                  </n-button>
                </div>
              </div>

              <div v-else-if="getFilterArgs(filter.name)?.length" class="filter-card__args">
                <div
                  v-for="(argDef, argIndex) in getFilterArgs(filter.name)"
                  :key="argDef.name"
                  class="filter-card__arg"
                >
                  <div class="filter-card__arg-header">
                    <span class="filter-card__arg-label">
                      {{ humanizeArgName(argDef.name) }}
                    </span>
                    <span v-if="argDef.default" class="filter-card__arg-hint">
                      Default: <code>{{ argDef.default }}</code>
                    </span>
                  </div>
                  <gaia-select
                    v-if="optionsForArg(argDef).length"
                    :options="optionsForArg(argDef)"
                    :render-label="renderArgOptionLabel"
                    :virtual-scroll="false"
                    :value="filter.args?.[argIndex] || null"
                    :placeholder="argDef.default || argDef.name"
                    size="small"
                    filterable
                    clearable
                    tag
                    @update:value="
                      value => updateFilterArg(filterIndex, argIndex, (value as string) ?? '')
                    "
                  />
                  <gaia-input
                    v-else
                    :value="filter.args?.[argIndex] ?? ''"
                    :placeholder="argDef.default || argDef.name"
                    size="small"
                    @update:value="value => updateFilterArg(filterIndex, argIndex, value)"
                  />
                </div>
              </div>

              <div v-if="getFilterExamples(filter.name).length" class="filter-card__examples">
                <span class="filter-card__examples-label">
                  {{ getFilterExamples(filter.name).length === 1 ? 'Example' : 'Examples' }}
                </span>
                <div
                  v-for="(example, exIdx) in getFilterExamples(filter.name)"
                  :key="exIdx"
                  class="filter-card__example"
                >
                  <code class="filter-card__example-input">{{ example.input }}</code>
                  <span class="filter-card__example-arrow">→</span>
                  <code class="filter-card__example-output">{{ example.output }}</code>
                  <span
                    v-if="example.args && example.args.length"
                    class="filter-card__example-args"
                    :title="`Arguments: ${example.args.join(', ')}`"
                  >
                    ({{ example.args.join(', ') }})
                  </span>
                </div>
              </div>

              <!-- One field, several tags: formatting normally changes this
                   tag only. "All N places" puts it on every tag of the field
                   when applied. -->
              <n-flex v-if="canApplyToAll" vertical :size="4" class="filter-card__scope">
                <n-flex align="center" justify="space-between" :size="8" :wrap="false">
                  <gaia-text size="meta" tone="subtle">Apply to</gaia-text>
                  <gaia-segmented
                    size="small"
                    :value="broadcastFilterNames.has(filter.name) ? 'all' : 'one'"
                    :options="scopeOptions"
                    @update:value="value => setBroadcast(filter.name, value === 'all')"
                  />
                </n-flex>
                <!-- Applying adds, it never takes away: a filter already on the
                     other tags stays there whatever this says, and would come
                     back as "all" next time the tag is opened. Said here rather
                     than left to be discovered. -->
                <gaia-text
                  v-if="
                    props.sharedFilterNames.includes(filter.name) &&
                    !broadcastFilterNames.has(filter.name)
                  "
                  size="micro"
                  tone="subtle"
                >
                  Already on all {{ props.siblingCount }} — “Only here” won’t take it off the
                  others; remove it on those tags instead.
                </gaia-text>
              </n-flex>
            </div>
          </div>
        </section>

        <n-collapse v-if="showHidden && scope === 'all'" class="config-drawer__advanced">
          <n-collapse-item name="advanced" title="Advanced options">
            <section class="config-drawer__section">
              <label class="config-drawer__field">
                <n-flex align="center" justify="space-between" :wrap="false">
                  <span class="config-drawer__label">Don't show in document</span>
                  <gaia-switch v-model:value="hidden" size="small" />
                </n-flex>
                <span class="config-drawer__field-hint">
                  Collect this value as an input but keep it out of the document — use it only in
                  conditions (e.g. <code>if age &lt; 18</code>). Inserts a
                  <code>{% set %}</code> statement.
                </span>
              </label>
            </section>
          </n-collapse-item>
        </n-collapse>
      </template>

      <section class="config-drawer__section config-drawer__output">
        <header class="config-drawer__output-head">
          <span class="config-drawer__section-title">Written in the document as</span>
          <n-tooltip placement="top" :delay="200">
            <template #trigger>
              <n-button
                size="tiny"
                quaternary
                class="config-drawer__copy-btn"
                :type="copied ? 'success' : 'default'"
                @click="copyPreview"
              >
                <template #icon>
                  <n-icon>
                    <IconCheck v-if="copied" />
                    <IconCopy v-else />
                  </n-icon>
                </template>
                {{ copied ? 'Copied' : 'Copy' }}
              </n-button>
            </template>
            Copy this snippet to your clipboard
          </n-tooltip>
        </header>
        <pre
          class="config-drawer__preview"
          :class="{ 'config-drawer__preview--loading': previewLoading }"
          >{{ displayedPreview }}</pre>
        <p v-if="previewError" class="config-drawer__preview-error">
          <n-icon class="config-drawer__preview-error-icon"><IconInfo /></n-icon>
          <span>{{ previewError }}</span>
        </p>
      </section>
    </n-flex>

    <n-flex
      v-if="annotation"
      class="config-form__footer"
      justify="space-between"
      align="center"
      :size="12"
    >
      <gaia-delete-button
        v-if="!previewOnly && !isExtractedBlock && !isFieldScope"
        intent="remove"
        variant="label"
        :subject="isOccurrenceScope ? 'this tag' : undefined"
        @click="emit('remove')"
      />
      <span v-else aria-hidden="true" />
      <n-flex :size="8">
        <template v-if="previewOnly">
          <n-button size="small" type="primary" @click="emit('cancel')">Done</n-button>
        </template>
        <template v-else>
          <n-button size="small" @click="handleCancel">{{
            embedded ? 'Reset' : 'Cancel'
          }}</n-button>
          <n-button v-if="!isBlockEnd" size="small" type="primary" @click="apply">Apply</n-button>
        </template>
      </n-flex>
    </n-flex>
  </div>
</template>

<style scoped>
/* The class names keep the `config-drawer__` prefix they had when this form
   lived inside the drawer — the drawer still hosts it, and a rename would
   touch every rule below for no visual gain. */
.config-form {
  display: flex;
  flex-direction: column;
}

.config-form__title {
  margin-bottom: 16px;
}

/* Column layout/gap handled by NFlex; padding kept. */
.config-drawer__body {
  padding: 18px 20px 24px;
}

.config-form--embedded .config-drawer__body {
  padding: 0;
}

/* In the drawer the actions stay pinned to the bottom of the sheet. */
.config-form__footer {
  position: sticky;
  bottom: 0;
  width: 100%;
  padding: 12px 20px;
  background: var(--tplcfg-surface);
  border-top: 1px solid var(--tplcfg-border);
}

.config-form--embedded .config-form__footer {
  position: static;
  padding: 14px 0 0;
  border-top: none;
}

.config-drawer__section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Sections inside the "Advanced options" collapse don't get the body flex's
   16px gap, so they'd sit flush — restore the spacing between them. */
.config-drawer__advanced :deep(.n-collapse-item__content-inner) {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.config-drawer__section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

/* Section titles and field labels share the design system's form-label idiom
   (sentence case, 12px/600) so the block reads like the guidance form above it. */
.config-drawer__section-title {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-text);
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.config-drawer__count-pill {
  font-size: var(--tplcfg-fs-nano);
  font-weight: var(--tplcfg-fw-bold);
  color: var(--tplcfg-accent);
  background: var(--tplcfg-accent-tint);
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.4;
}

.config-drawer__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.config-drawer__label {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-text);
}

.config-drawer__field-hint {
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  line-height: 1.4;
}

.config-drawer__field-hint--error {
  color: var(--tplcfg-alert);
  font-weight: var(--tplcfg-fw-medium);
}

.selection-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.selection-options__row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.selection-options__handle {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-faint);
  font-variant-numeric: tabular-nums;
  min-width: 18px;
  text-align: right;
}

.selection-options__add {
  margin-top: 2px;
}

.selection-options__empty {
  margin: 4px 0 0;
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-faint);
  line-height: 1.4;
}

.config-drawer__binding {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 14px;
  background: var(--tplcfg-accent-wash);
  border: 1px solid var(--tplcfg-accent-tint);
  border-radius: 10px;
}

.config-drawer__binding-summary {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.config-drawer__binding-path {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  background: var(--tplcfg-surface);
  padding: 3px 8px;
  border-radius: 6px;
  border: 1px solid var(--tplcfg-accent-border);
  color: var(--tplcfg-accent-strong);
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-all;
  white-space: normal;
  line-height: 1.4;
}

.config-drawer__alias-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.config-drawer__readonly-note {
  margin: 0;
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  line-height: 1.55;
}

.config-drawer__readonly-note code {
  background: var(--tplcfg-muted);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: var(--tplcfg-fs-meta);
  font-family: var(--tplcfg-mono);
  color: var(--tplcfg-ink);
}

.config-drawer__inline-keyword {
  display: flex;
  justify-content: center;
  margin: -4px 0;
}

.config-drawer__keyword-pill {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  letter-spacing: var(--tplcfg-track-caps);
  color: var(--tplcfg-text);
  background: var(--tplcfg-muted);
  border: 1px solid var(--tplcfg-border);
  padding: 2px 12px;
  border-radius: 999px;
}

.config-drawer__filters-empty {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px;
  background: var(--tplcfg-soft);
  border: 1px dashed var(--tplcfg-border);
  border-radius: 8px;
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  line-height: 1.55;
}

.config-drawer__filters-empty code {
  background: var(--tplcfg-border);
  padding: 1px 5px;
  border-radius: 4px;
  font-size: var(--tplcfg-fs-meta);
  font-family: var(--tplcfg-mono);
  color: var(--tplcfg-ink);
}

.config-drawer__filters-empty-icon {
  color: var(--tplcfg-faint);
  flex-shrink: 0;
  margin-top: 1px;
}

/* ─── Filter cards ─────────────────────────────────────────────────── */

.config-drawer__filter-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 16px;
  background: var(--tplcfg-soft);
  border: 1px solid var(--tplcfg-border);
  border-radius: 10px;
}

.filter-card__head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.filter-card__head-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex: 1 1 auto;
}

/* What the formatting does, in words, leads; the technical name follows for
   authors who know it from their Word tags. */
.filter-card__title {
  font-size: var(--tplcfg-fs-row);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-ink);
  line-height: 1.4;
}

.filter-card__name {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-subtle);
  word-break: break-word;
}

.filter-card__scope {
  padding-top: 10px;
  border-top: 1px dashed var(--tplcfg-border);
}

.filter-card__args {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.filter-card__arg {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-card__arg-header {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-card__arg-label {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-text);
}

.filter-card__arg-hint {
  font-size: var(--tplcfg-fs-nano);
  color: var(--tplcfg-faint);
  font-weight: var(--tplcfg-fw-regular);
  letter-spacing: 0;
  text-transform: none;
}

.filter-card__arg-hint code {
  font-family: var(--tplcfg-mono);
  background: var(--tplcfg-surface);
  border: 1px solid var(--tplcfg-border);
  padding: 0 4px;
  border-radius: 3px;
  color: var(--tplcfg-text);
  margin-left: 2px;
}

.arg-option {
  padding: 2px 0;
  white-space: normal;
  line-height: 1.3;
}

.arg-option__label {
  font-size: var(--tplcfg-fs-row);
  color: var(--tplcfg-ink);
}

.arg-option__desc {
  font-size: var(--tplcfg-fs-meta);
  color: var(--tplcfg-faint);
}

.filter-card__variadic {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filter-card__variadic-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-card__variadic-handle {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-semibold);
  color: var(--tplcfg-faint);
  font-variant-numeric: tabular-nums;
  min-width: 18px;
  text-align: right;
}

.filter-card__variadic-add {
  margin-top: 2px;
}

.filter-card__examples {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px dashed var(--tplcfg-border);
}

.filter-card__examples-label {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-medium);
  color: var(--tplcfg-subtle);
}

.filter-card__example {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--tplcfg-fs-meta);
  flex-wrap: wrap;
}

.filter-card__example-input,
.filter-card__example-output {
  font-family: var(--tplcfg-mono);
  padding: 1px 6px;
  border-radius: 4px;
  font-size: var(--tplcfg-fs-meta);
}

.filter-card__example-input {
  background: var(--tplcfg-border);
  color: var(--tplcfg-text);
}

.filter-card__example-output {
  background: var(--tplcfg-entity-border);
  color: var(--tplcfg-entity-fg);
}

.filter-card__example-arrow {
  color: var(--tplcfg-faint);
}

/* The argument values that produced this example (e.g. "(EUR, en)") — so the
   examples for multi-arg filters like format_currency aren't ambiguous. */
.filter-card__example-args {
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-nano);
  color: var(--tplcfg-subtle);
}

/* ─── Output preview ───────────────────────────────────────────────── */

/* In the drawer the preview stays in view while the filter cards scroll. */
.config-drawer__output {
  position: sticky;
  bottom: -24px;
  background: var(--tplcfg-surface);
  padding-top: 8px;
  border-top: 1px dashed var(--tplcfg-border);
  margin-top: 4px;
}

.config-form--embedded .config-drawer__output {
  position: static;
}

.config-drawer__output-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.config-drawer__copy-btn {
  font-size: var(--tplcfg-fs-meta);
  font-weight: var(--tplcfg-fw-medium);
}

.config-drawer__preview {
  margin: 0;
  padding: 10px 12px;
  background: var(--tplcfg-soft);
  border: 1px solid var(--tplcfg-border);
  color: var(--tplcfg-ink);
  border-radius: 8px;
  font-family: var(--tplcfg-mono);
  font-size: var(--tplcfg-fs-meta);
  line-height: 1.55;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
  transition: opacity 0.15s ease;
}

.config-drawer__preview--loading {
  opacity: 0.6;
}

.config-drawer__preview-error {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  padding: 8px 10px;
  border-radius: 6px;
  background: var(--tplcfg-alert-surface);
  border: 1px solid var(--tplcfg-alert-border);
  color: var(--tplcfg-alert);
  font-size: var(--tplcfg-fs-meta);
  line-height: 1.4;
  word-break: break-word;
}

.config-drawer__preview-error-icon {
  flex-shrink: 0;
  margin-top: 1px;
}
</style>
